import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Loader2, Pencil } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';

interface EditableNameProps {
  value: string;
  documentId: string;
  onUpdate: (newName: string) => void;
  className?: string;
  placeholder?: string;
  maxLength?: number;
  field?: 'name' | 'company_name' | 'document_period'; // Campo a actualizar en la API
}

const EditableName: React.FC<EditableNameProps> = ({
  value,
  documentId,
  onUpdate,
  className = '',
  placeholder = 'Nombre corto...',
  maxLength = 100,
  field = 'name',
}) => {
  const { theme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Update local value when prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(value);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setError(null);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // Validate length (permitir vacío para eliminar nombre corto)
    if (trimmedValue.length > maxLength) {
      setError(`El nombre no puede tener más de ${maxLength} caracteres`);
      return;
    }

    // If unchanged, just cancel
    if (trimmedValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updates: { name?: string; company_name?: string; document_period?: string } = {};
      updates[field] = trimmedValue || undefined;

      const response = await apiService.updateDocument(documentId, updates);

      if (response.success && response.document) {
        onUpdate(trimmedValue);
        setIsEditing(false);
      } else {
        setError('Error al guardar el nombre');
      }
    } catch (err: any) {
      console.error('Error updating document name:', err);
      setError(err.message || 'Error al guardar el nombre');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isSaving}
          maxLength={maxLength}
          className={`flex-1 px-2 py-1 text-sm border rounded ${theme.colors.bg.input} ${theme.colors.text.primary} ${theme.colors.border.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
          placeholder={placeholder}
        />
        {isSaving ? (
          <Loader2 className="w-4 h-4 animate-spin text-nb-accent" />
        ) : (
          <>
            <button
              type="button"
              onClick={handleSave}
              className={`p-1 rounded hover:${theme.colors.bg.secondary} transition-colors`}
              title="Guardar (Enter)"
            >
              <Check className="w-4 h-4 text-nb-success" />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className={`p-1 rounded hover:${theme.colors.bg.secondary} transition-colors`}
              title="Cancelar (Esc)"
            >
              <X className="w-4 h-4 text-nb-error" />
            </button>
          </>
        )}
        {error && <span className="text-xs text-nb-error ml-2">{error}</span>}
      </div>
    );
  }

  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <div
      onClick={handleStartEdit}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative flex items-center gap-1.5 cursor-text whitespace-nowrap truncate ${
        isEmpty ? 'opacity-50 italic' : ''
      } ${className}`}
      title="Click para editar"
    >
      <span className="truncate flex-1">{displayValue}</span>
      <Pencil
        className={`w-3 h-3 transition-opacity duration-200 flex-shrink-0 ${
          theme.colors.text.secondary
        } ${isHovered ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
};

export default EditableName;
